import React, { useEffect, useRef } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from "vexflow";
import { parseMusicXml, type MusicXmlNote } from "../lib/guitarEngine";

interface VexFlowRendererProps {
  musicXml?: string;
  notes?: MusicXmlNote[];
  currentNoteIndex?: number;
  width?: number;
  height?: number;
}

export const VexFlowRenderer: React.FC<VexFlowRendererProps> = ({
  musicXml,
  notes: externalNotes,
  currentNoteIndex = -1,
  width = 600,
  height = 200,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const notes = externalNotes ?? (musicXml ? parseMusicXml(musicXml) : []);

  useEffect(() => {
    if (!containerRef.current || notes.length === 0) return;

    containerRef.current.innerHTML = "";

    const renderer = new Renderer(containerRef.current, Renderer.Backends.SVG);
    renderer.resize(width, height);

    const context = renderer.getContext();

    // Crear compás
    const stave = new Stave(10, 20, width - 20);
    stave.addClef("treble").addTimeSignature("4/4").setContext(context).draw();

    // Convertir notas MusicXml a StaveNotes
    const vexNotes: InstanceType<typeof StaveNote>[] = [];

    for (let i = 0; i < notes.length; i++) {
      const note = notes[i];
      const isCurrent = i === currentNoteIndex;
      const keys = [`${note.step.toLowerCase()}/${note.octave}`];

      const staveNote = new StaveNote({
        keys,
        duration: note.type === "quarter" ? "q" : note.type === "half" ? "h" : "8",
        autoStem: true,
      });

      // Añadir alteración si es necesario
      if (note.alter === 1) {
        staveNote.addModifier(new Accidental("#"), 0);
      } else if (note.alter === -1) {
        staveNote.addModifier(new Accidental("b"), 0);
      }

      // Resaltar nota actual
      if (isCurrent) {
        staveNote.setStyle({ fillStyle: "#e74c3c", strokeStyle: "#e74c3c" });
      }

      vexNotes.push(staveNote);
    }

    // Crear voz y dibujar
    const voice = new Voice({ numBeats: 4, beatValue: 4 });
    voice.addTickables(vexNotes);

    const formatter = new Formatter();
    formatter.joinVoices([voice]).format([voice], width - 40);

    voice.draw(context, stave);

    // Dibujar indicador de progreso
    if (currentNoteIndex >= 0 && currentNoteIndex < notes.length) {
      const noteX = 40 + (currentNoteIndex * ((width - 80) / notes.length));
      context.save();
      context.setFillStyle("rgba(231, 76, 60, 0.2)");
      context.fillRect(noteX, 10, (width - 80) / notes.length, 160);
      context.restore();
    }
  }, [notes, currentNoteIndex, width, height]);

  return (
    <div
      ref={containerRef}
      className="border rounded-xl"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: "#FAF6F0",
        borderColor: "#E8E0D4",
        borderWidth: 1,
        borderStyle: "solid",
        borderRadius: "12px",
        boxShadow: "inset 0 1px 4px rgba(0,0,0,0.04)",
      }}
    />
  );
};

export default VexFlowRenderer;
